import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Move, Plus, Trash2, Save, Edit3, Eye, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STATUS_BG = {
  libre: 'bg-[hsl(var(--card))] border-[hsl(var(--border))]',
  ocupada: 'bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]',
  cuenta_solicitada: 'bg-[hsl(43_92%_55%)] text-white border-[hsl(43_92%_55%)]',
  reservada: 'bg-[hsl(199_70%_55%)] text-white border-[hsl(199_70%_55%)]',
};

export default function FloorPlan() {
  const { t, lang } = useI18n();
  const [tables, setTables] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [activeZone, setActiveZone] = useState('Salón');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const containerRef = useRef(null);
  const draggingRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get('/tables');
    setTables(r.data);
    // Detect zones and switch to the first one if current isn't there
    const newZones = [...new Set(r.data.map(tt => tt.zona).filter(Boolean))];
    setActiveZone(prev => (newZones.length && !newZones.includes(prev)) ? newZones[0] : prev);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Memoized: avoid recomputing zones/filtered on every render
  const zones = useMemo(
    () => [...new Set([...tables.map(tt => tt.zona).filter(Boolean), 'Salón'])],
    [tables],
  );
  const filtered = useMemo(
    () => tables.filter(tt => (tt.zona || 'Salón') === activeZone),
    [tables, activeZone],
  );

  // Drag handling
  const onPointerDown = (e, table) => {
    if (!editMode) return;
    e.preventDefault();
    const tile = e.currentTarget;
    const rect = containerRef.current.getBoundingClientRect();
    const tileRect = tile.getBoundingClientRect();
    draggingRef.current = {
      id: table.id,
      offsetX: e.clientX - tileRect.left,
      offsetY: e.clientY - tileRect.top,
      containerRect: rect,
    };
    tile.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!editMode || !draggingRef.current) return;
    const d = draggingRef.current;
    const x = e.clientX - d.containerRect.left - d.offsetX;
    const y = e.clientY - d.containerRect.top - d.offsetY;
    const cx = Math.max(0, Math.min(d.containerRect.width - 80, x));
    const cy = Math.max(0, Math.min(d.containerRect.height - 80, y));
    setTables(prev => prev.map(tt => tt.id === d.id ? { ...tt, posicion_x: Math.round(cx), posicion_y: Math.round(cy) } : tt));
  };

  const onPointerUp = async (e, table) => {
    if (!editMode || !draggingRef.current) return;
    const cur = tables.find(tt => tt.id === draggingRef.current.id);
    draggingRef.current = null;
    if (cur) {
      await api.put(`/tables/${cur.id}/position`, { posicion_x: cur.posicion_x, posicion_y: cur.posicion_y, zona: cur.zona });
    }
  };

  const onTableClick = (table) => {
    if (editMode) {
      setSelectedTable(table);
      return;
    }
    // Service mode -> jump to POS to open table
    window.location.assign('/pos');
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="floor-plan-page">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-[hsl(var(--card))]">
        <div className="font-heading font-semibold text-lg flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-[hsl(var(--primary))]" />
          {t('nav_floor_plan')}
        </div>
        <div className="flex gap-1 ml-auto">
          {zones.map(z => (
            <Button key={z} variant={activeZone === z ? 'default' : 'outline'} size="sm" onClick={() => setActiveZone(z)} data-testid={`floor-zone-${z}`}>
              {z}
            </Button>
          ))}
          <Button variant={editMode ? 'default' : 'outline'} size="sm" onClick={() => setEditMode(m => !m)} className="ml-2" data-testid="floor-edit-toggle">
            {editMode ? <Save className="h-3.5 w-3.5 mr-1" /> : <Edit3 className="h-3.5 w-3.5 mr-1" />}
            {editMode ? (lang === 'es' ? 'Terminar' : 'Terminar') : (lang === 'es' ? 'Editar' : 'Editar')}
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="floor-create-table-button">
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('create_table')}
          </Button>
        </div>
      </div>

      <div className="px-4 py-2 text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))]"></span>{t('table_status_libre')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--secondary))]"></span>{t('table_status_ocupada')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(43_92%_55%)]"></span>{t('table_status_cuenta_solicitada')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(199_70%_55%)]"></span>{t('table_status_reservada')}</span>
        {editMode && <span className="ml-auto italic">{lang === 'es' ? 'Modo edición: arrastra las mesas para colocarlas. Click para editar.' : 'Modo edição: arraste as mesas para posicionar. Clique para editar.'}</span>}
      </div>

      <div
        ref={containerRef}
        className={cn(
          'flex-1 relative overflow-auto bg-[hsl(var(--muted))]/30',
          editMode ? 'cursor-crosshair' : ''
        )}
        style={{
          backgroundImage: editMode ? 'radial-gradient(hsl(var(--border)) 1px, transparent 1px)' : 'none',
          backgroundSize: editMode ? '20px 20px' : 'auto',
        }}
        onPointerMove={onPointerMove}
      >
        {filtered.length === 0 ? (
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <LayoutGrid className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                {lang === 'es' ? 'Sin mesas en esta zona. Crea la primera o cambia de zona.' : 'Sem mesas nesta zona. Crie a primeira ou troque de zona.'}
              </p>
              <Button onClick={() => setCreateOpen(true)} size="sm"><Plus className="h-3 w-3 mr-1" /> {t('create_table')}</Button>
            </div>
          </div>
        ) : filtered.map(table => (
          <button
            key={table.id}
            onPointerDown={(e) => onPointerDown(e, table)}
            onPointerUp={(e) => onPointerUp(e, table)}
            onClick={() => !editMode && onTableClick(table)}
            data-testid={`floor-table-${table.numero}`}
            className={cn(
              'absolute h-20 w-20 rounded-2xl border-2 grid place-items-center font-heading font-bold transition-shadow',
              STATUS_BG[table.estado] || STATUS_BG.libre,
              editMode ? 'cursor-grab active:cursor-grabbing shadow-md' : 'hover:shadow-lg cursor-pointer',
              draggingRef.current?.id === table.id && 'shadow-2xl scale-105 z-10',
            )}
            style={{
              left: `${table.posicion_x || 20}px`,
              top: `${table.posicion_y || 20}px`,
              touchAction: 'none',
            }}
          >
            <div className="text-2xl leading-none">{table.numero}</div>
            <div className="text-[10px] mt-0.5 opacity-80">{table.capacidad}p</div>
          </button>
        ))}
      </div>

      <CreateTableDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        zones={zones}
        defaultZone={activeZone}
        t={t}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
      <EditTableDialog
        open={!!selectedTable}
        onOpenChange={(o) => !o && setSelectedTable(null)}
        table={selectedTable}
        zones={zones}
        t={t}
        onSaved={() => { setSelectedTable(null); load(); }}
      />
    </div>
  );
}

function CreateTableDialog({ open, onOpenChange, zones, defaultZone, t, onCreated }) {
  const [form, setForm] = useState({ numero: '', nombre: '', capacidad: 4, zona: defaultZone, posicion_x: 40, posicion_y: 40 });
  useEffect(() => { setForm(f => ({ ...f, zona: defaultZone })); }, [defaultZone, open]);
  const save = async () => {
    await api.post('/tables', form);
    toast.success('OK');
    onCreated();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="floor-create-dialog">
        <DialogHeader><DialogTitle>{t('create_table')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t('table_number')}</Label><Input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} data-testid="floor-create-number" /></div>
          <div><Label>{t('capacity')}</Label><Input type="number" min={1} value={form.capacidad} onChange={e => setForm({ ...form, capacidad: Number(e.target.value) })} /></div>
          <div className="col-span-2"><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="opcional" /></div>
          <div className="col-span-2"><Label>{t('zone')}</Label>
            <Select value={form.zona} onValueChange={v => setForm({ ...form, zona: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                <SelectItem value="Salón">Salón</SelectItem>
                <SelectItem value="Terraza">Terraza</SelectItem>
                <SelectItem value="Barra">Barra</SelectItem>
                <SelectItem value="Privado">Privado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.numero} data-testid="floor-create-save">{t('save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditTableDialog({ open, onOpenChange, table, zones, t, onSaved }) {
  const [form, setForm] = useState({ numero: '', nombre: '', capacidad: 4, zona: 'Salón', posicion_x: 0, posicion_y: 0 });
  useEffect(() => {
    if (table) setForm({
      numero: table.numero || '', nombre: table.nombre || '',
      capacidad: table.capacidad || 4, zona: table.zona || 'Salón',
      posicion_x: table.posicion_x || 0, posicion_y: table.posicion_y || 0,
    });
  }, [table, open]);
  if (!table) return null;
  const save = async () => {
    await api.put(`/tables/${table.id}`, form);
    toast.success('OK');
    onSaved();
  };
  const remove = async () => {
    if (!confirm('Borrar esta mesa?')) return;
    await api.delete(`/tables/${table.id}`);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="floor-edit-dialog">
        <DialogHeader><DialogTitle>Mesa {table.numero}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t('table_number')}</Label><Input value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} /></div>
          <div><Label>{t('capacity')}</Label><Input type="number" min={1} value={form.capacidad} onChange={e => setForm({ ...form, capacidad: Number(e.target.value) })} /></div>
          <div className="col-span-2"><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} /></div>
          <div className="col-span-2"><Label>{t('zone')}</Label>
            <Select value={form.zona} onValueChange={v => setForm({ ...form, zona: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Salón">Salón</SelectItem>
                <SelectItem value="Terraza">Terraza</SelectItem>
                <SelectItem value="Barra">Barra</SelectItem>
                <SelectItem value="Privado">Privado</SelectItem>
                {zones.filter(z => !['Salón', 'Terraza', 'Barra', 'Privado'].includes(z)).map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>X (px)</Label><Input type="number" value={form.posicion_x} onChange={e => setForm({ ...form, posicion_x: Number(e.target.value) })} /></div>
          <div><Label>Y (px)</Label><Input type="number" value={form.posicion_y} onChange={e => setForm({ ...form, posicion_y: Number(e.target.value) })} /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="destructive" onClick={remove} data-testid="floor-edit-delete"><Trash2 className="h-4 w-4" /></Button>
          <Button onClick={save} data-testid="floor-edit-save">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
